import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
export interface KennisbankArticleProps {
  // Basis
  hero_title: string;
  hero_subtitle: string;
  intro_text: React.ReactNode;
  local_context: React.ReactNode;
  // Voordelen
  benefits_title: string;
  benefits_block: Array<{
    title: string;
    text: string;
  }>;
  // Proces
  process_title: string;
  process_block: Array<{
    title: string;
    text: string;
  }>;
  // Bewijs
  case_section_title: string;
  case_section_text: React.ReactNode;
  // FAQ
  faq_intro: string;
  faq_q1?: string;
  faq_a1?: string;
  faq_q2?: string;
  faq_a2?: string;
  faq_q3?: string;
  faq_a3?: string;
  faq_q4?: string;
  faq_a4?: string;
  faq_q5?: string;
  faq_a5?: string;
  // CTA
  cta_title: string;
  cta_text: string;
  cta_button_text: string;
  cta_button_link: string;
  // SEO
  seo_title: string;
  meta_description: string;
  schema_type: string;
  schema_json: string;
  // Linkblokken
  internal_links_services: Array<{
    label: string;
    url: string;
  }>;
  internal_links_cities: Array<{
    label: string;
    url: string;
  }>;
  related_articles: Array<{
    label: string;
    url: string;
  }>;
  // Extra (handig voor de template weergave)
  breadcrumb_label?: string;
  reading_time?: string;
}
export function KennisbankArticleTemplate({
  hero_title,
  hero_subtitle,
  intro_text,
  local_context,
  benefits_title,
  benefits_block,
  process_title,
  process_block,
  case_section_title,
  case_section_text,
  faq_intro,
  faq_q1,
  faq_a1,
  faq_q2,
  faq_a2,
  faq_q3,
  faq_a3,
  faq_q4,
  faq_a4,
  faq_q5,
  faq_a5,
  cta_title,
  cta_text,
  cta_button_text,
  cta_button_link,
  seo_title,
  meta_description,
  schema_type,
  schema_json,
  internal_links_services,
  internal_links_cities,
  related_articles,
  breadcrumb_label = 'Artikel',
  reading_time = '5 min leestijd'
}: KennisbankArticleProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = seo_title;
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = meta_description;
    // Inject Schema JSON-LD
    if (schema_json) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = schema_json;
      script.id = 'article-schema';
      document.head.appendChild(script);
      return () => {
        const el = document.getElementById('article-schema');
        if (el) el.remove();
      };
    }
  }, [seo_title, meta_description, schema_json]);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  // Verzamel FAQ's dynamisch
  const faqs = [];
  if (faq_q1 && faq_a1) faqs.push({
    q: faq_q1,
    a: faq_a1
  });
  if (faq_q2 && faq_a2) faqs.push({
    q: faq_q2,
    a: faq_a2
  });
  if (faq_q3 && faq_a3) faqs.push({
    q: faq_q3,
    a: faq_a3
  });
  if (faq_q4 && faq_a4) faqs.push({
    q: faq_q4,
    a: faq_a4
  });
  if (faq_q5 && faq_a5) faqs.push({
    q: faq_q5,
    a: faq_a5
  });
  return <main className="pt-16 lg:pt-[72px]" data-id="element-625" data-mp="1369xsymxkel6">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-626" data-mp="p7a4vesdxdj7">
        <div className="absolute inset-0 pointer-events-none" data-id="element-627" data-mp="u7xym88008h4">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-628" data-mp="u51z4fgi5bqk" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-629" data-mp="k8yvf560551i" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-630" data-mp="29nwxesvq6s4p" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-631" data-mp="aivmh7vw9u9m">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-632" data-mp="5j97aswia3y6">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-633" data-mp="k61v7cm5vpo">
              Home
            </a>
            <span className="text-navy-500" data-id="element-634" data-mp="3n0tp80ech03a">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-635" data-mp="3mz8xsjnbtx9n">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-636" data-mp="3e16prk56e4cv">/</span>
            <span className="text-accent-light font-medium" data-id="element-637" data-mp="3dyasly15jjph">
              {breadcrumb_label}
            </span>
          </nav>

          {/* ACF: hero_title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up-delay-1" data-id="element-638" data-mp="34395f7qfsrpj">
            {hero_title}
          </h1>

          {/* ACF: hero_subtitle */}
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-2" data-id="element-639" data-mp="2yvsswhif9z95">
            {hero_subtitle}
          </p>
        </div>
      </section>

      {/* Main Content Area - Single Column */}
      <div className="bg-white py-16 lg:py-24" data-id="element-640" data-mp="38t78kewiuk0e">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-641" data-mp="2b8aysfboqp30">
          {/* Intro & Local Context */}
          <div className="prose prose-lg prose-slate max-w-none mb-16" data-id="element-642" data-mp="2g7nwjv43hgkd">
            {intro_text}

            <h2 className="text-2xl font-bold text-navy-900 mt-12 mb-4" data-id="element-643" data-mp="2l4o1g2p7kd9w">
              Lokale impact
            </h2>
            <div className="text-slate-700 leading-relaxed" data-id="element-644" data-mp="2pxyzbec1ljpo">
              {local_context}
            </div>
          </div>

          {/* Voordelen (benefits_block) */}
          <div className="mb-16" data-id="element-645" data-mp="1xfbk2fjq8ywi">
            <h2 className="text-3xl font-bold text-navy-900 mb-8" data-id="element-646" data-mp="1xejvvpsgm3g9">
              {benefits_title}
            </h2>
            <div className="space-y-8" data-id="element-647" data-mp="1pdc9m2yss34j">
              {benefits_block.map((benefit, index) => <div key={index} data-id="element-648" data-mp="1ke9fhdkcvhbc" data-mpi={index}>
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-649" data-mp="1z5rt54pqszrj">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-700 leading-relaxed text-lg" data-id="element-650" data-mp="1u7gk2w79utad">
                    {benefit.text}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Proces (process_block) */}
          <div className="mb-16" data-id="element-651" data-mp="11juifthltyq5">
            <h2 className="text-3xl font-bold text-navy-900 mb-8" data-id="element-652" data-mp="1gf0o2fcsfviy">
              {process_title}
            </h2>
            <div className="space-y-8" data-id="element-653" data-mp="1bemcwofw45gp">
              {process_block.map((step, index) => <div key={index} data-id="element-654" data-mp="2rsuojnz4gudk" data-mpi={index}>
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-655" data-mp="2rs2uwip356px">
                    {index + 1}. {step.title}
                  </h3>
                  <p className="text-slate-700 leading-relaxed text-lg" data-id="element-656" data-mp="2uelprt1fqhvo">
                    {step.text}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Bewijs / Case Section */}
          <div className="mb-16" data-id="element-657" data-mp="2leyttmrh64a0">
            <h2 className="text-3xl font-bold text-navy-900 mb-6" data-id="element-658" data-mp="2lg077xvyr2uw">
              {case_section_title}
            </h2>
            <div className="prose prose-lg prose-slate max-w-none" data-id="element-659" data-mp="3d53dfj7euna6">
              {case_section_text}
            </div>
          </div>

          {/* FAQ Section */}
          {faqs.length > 0 && <div className="mb-16" data-id="element-660" data-mp="34crm7v4h2vt5">
              <h2 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-661" data-mp="34bqb5qngi9bf">
                Veelgestelde vragen
              </h2>
              <p className="text-lg text-slate-700 mb-8" data-id="element-662" data-mp="38cab11zezr19">{faq_intro}</p>

              <div className="space-y-4" data-id="element-663" data-mp="3w2w3vm0ul4qo">
                {faqs.map((faq, index) => <div key={index} className="border-b border-navy-100 last:border-0" data-id="element-664" data-mp="4q1sif6of9je" data-mpi={index}>
                    <button onClick={() => toggleFaq(index)} className="w-full py-5 flex items-center justify-between text-left focus:outline-none group" data-id="element-665" data-mp="3c5umjwxtclxi">
                      <span className="font-bold text-navy-900 text-lg group-hover:text-accent transition-colors" data-id="element-666" data-mp="2xf3xuxriq2ym">
                        {faq.q}
                      </span>
                      {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0 ml-4" data-id="element-667" data-mp="3q39kxr61o41m" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0 ml-4 group-hover:text-accent transition-colors" data-id="element-668" data-mp="3l34yzlmdah40" />}
                    </button>
                    {openFaq === index && <div className="pb-5 text-slate-700 leading-relaxed text-lg" data-id="element-669" data-mp="29mwvnhz431ra">
                        {faq.a}
                      </div>}
                  </div>)}
              </div>
            </div>}
        </div>
      </div>

      {/* Linkblokken */}
      <section className="py-16 bg-surface-muted border-t border-navy-100" data-id="element-670" data-mp="p4w23bur1gur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-671" data-mp="u50nngail1ru">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-672" data-mp="yy3hzqn48f93">
            {/* ACF: internal_links_services */}
            <div data-id="element-673" data-mp="13vlhyrj5nk4y">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-674" data-mp="13y7oqoy81vm4">
                Gerelateerde diensten
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-675" data-mp="1cwjphjssy9sa">
                {internal_links_services.map((link, i) => <a key={i} href={link.url} className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-676" data-mp="1hul700aky11n" data-mpi={i}>
                    {link.label}
                  </a>)}
              </nav>
            </div>

            {/* ACF: internal_links_cities */}
            <div data-id="element-677" data-mp="27aw726w6ql3r">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-678" data-mp="27ae1ukrjowjp">
                Lokaal webdesign
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-679" data-mp="2c8pbovhub71x">
                {internal_links_cities.map((link, i) => <a key={i} href={link.url} className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-680" data-mp="2g9hn9ve6po1d" data-mpi={i}>
                    {link.label}
                  </a>)}
              </nav>
            </div>

            {/* ACF: related_articles */}
            <div data-id="element-681" data-mp="2zzupz684tra4">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-682" data-mp="2zy9wljxk0edu">
                Meer lezen
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-683" data-mp="3909cg8wu1cf7">
                {related_articles.map((link, i) => <a key={i} href={link.url} className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-684" data-mp="xcwb7ulnncrq" data-mpi={i}>
                    {link.label}
                  </a>)}
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Custom CTA (overrides FinalCtaSection for this template) */}
      <section className="py-24 bg-accent text-white text-center px-4" data-id="element-685" data-mp="1g3xe2tpwbsgk">
        <div className="max-w-3xl mx-auto" data-id="element-686" data-mp="1c1sjnsrmirsv">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-687" data-mp="1c2vda8gbz8ch">{cta_title}</h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-688" data-mp="17akdd5llbmjx">{cta_text}</p>
          <a href={cta_button_link} className="inline-flex items-center gap-2 bg-white text-accent hover:bg-navy-50 font-bold px-8 py-4 rounded-button shadow-lg transition-all duration-200 text-lg" data-id="element-689" data-mp="178r2fe3y2g64">
            {cta_button_text}
            <ArrowRightIcon className="w-5 h-5" data-id="element-690" data-mp="1uz3jbeh63n8u" />
          </a>
        </div>
      </section>
    </main>;
}